import { Injectable, signal, effect, PLATFORM_ID, inject, computed } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

export type Theme = 'light' | 'dark';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private static readonly STORAGE_KEY = 'elev-rms-theme';
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  /** Current active theme */
  readonly theme = signal<Theme>(this.getInitialTheme());

  /** Whether dark mode is active */
  readonly isDark = computed(() => this.theme() === 'dark');

  constructor() {
    // Apply theme to DOM whenever the signal changes, and persist to localStorage
    effect(() => {
      const current = this.theme();
      if (this.isBrowser) {
        document.documentElement.setAttribute('data-theme', current);
        const iconLink = document.querySelector<HTMLLinkElement>('link[rel="icon"][type="image/svg+xml"]');
        if (iconLink) {
          iconLink.href = current === 'dark' ? 'favicon-dark.svg' : 'favicon-light.svg';
        }
        const themeMeta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
        if (themeMeta) {
          themeMeta.content = current === 'dark' ? '#0f172a' : '#0b2c51';
        }
        try {
          localStorage.setItem(ThemeService.STORAGE_KEY, current);
        } catch {
          // localStorage may be unavailable
        }
      }
    });
  }

  /** Toggle between light and dark */
  toggle(): void {
    this.theme.update(t => t === 'light' ? 'dark' : 'light');
  }

  /** Set a specific theme */
  setTheme(theme: Theme): void {
    this.theme.set(theme);
  }

  private getInitialTheme(): Theme {
    if (!this.isBrowser) {
      return 'light';
    }

    try {
      const stored = localStorage.getItem(ThemeService.STORAGE_KEY);
      if (stored === 'dark' || stored === 'light') {
        return stored;
      }
    } catch {
      // localStorage may be unavailable
    }

    // Respect OS preference
    if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }

    return 'light';
  }
}

